/**
 * Iframe postMessage gating primitives.
 *
 * The preview-surface postMessage threat model has three recurring
 * shapes; this file centralizes them so future security tweaks land
 * once instead of being scattered across handler bodies (see review
 * pass #4, where the `requestSnapshot` per-call listener was missed
 * because it lived outside the main handler I'd hardened).
 *
 * Shapes:
 *
 *   1. Main handler — long-lived `window.addEventListener('message')`
 *      that dispatches on `e.data.type`. Used by HtmlViewer's bridge
 *      for papan:vs / papan:nav / papan:openFile / papan:openExternal
 *      / papan:handshake / papan:run. Source must match one of OUR
 *      iframe refs; bridge-message types additionally require an
 *      origin match (defense-in-depth against attacker HTML running
 *      inside our legitimate iframe).
 *
 *   2. Per-call listener — temporary handler installed inside a
 *      Promise constructor or interval callback (HtmlViewer's
 *      `requestSnapshot`, CollabDocModifier's view-state capture).
 *      Source must match a specific captured window — the iframe we
 *      sent the request to.
 *
 *   3. Warn-once latch — diagnostic for handlers that delegate to
 *      optional consumer callbacks. The first time a message arrives
 *      without a wired callback, log a console.warn naming the prop;
 *      subsequent messages stay silent. Defends consumers of
 *      `@papan/collab/ui` from "wired wrong, silently does nothing"
 *      bugs (the openExternal / openFile cases).
 *
 * SDK plan (`docs/plans/2026-05-23_PAPAN_SDK_V1.md`) anticipates
 * extending the main handler to ~10–15 message types across the
 * reserved namespaces (files, events, keybook, share, ai, ui, auth,
 * scripts, user, org) plus nested-iframe / chain support. Centralizing
 * here means new types add a row to a dispatcher map instead of
 * inlining a gate; the missed-listener footgun goes away by
 * construction.
 */
import { useEffect, useRef, type RefObject } from 'react'

// ─── Source predicates ──────────────────────────────────────────────────────

/**
 * Predicate: returns true if a MessageEvent source is trusted. Two
 * built-in factories (`acceptIframeRefs`, `acceptExpectedSource`)
 * cover the common cases; custom predicates are fine for future
 * shapes (e.g. "any descendant iframe" when chains land).
 */
export type SourceAcceptor = (source: MessageEventSource | null) => boolean

/**
 * Build a SourceAcceptor that matches any of the provided iframe refs'
 * `contentWindow`. Read at dispatch time so iframes that mount after
 * the listener is registered are picked up automatically.
 *
 * Use when a component owns one or more iframes (HtmlViewer's
 * double-buffered iframe0/iframe1, CollabDocModifier's single iframe).
 */
export function acceptIframeRefs(
  ...refs: Array<RefObject<HTMLIFrameElement | null>>
): SourceAcceptor {
  return (source) => {
    if (!source) return false
    return refs.some(ref => ref.current?.contentWindow === source)
  }
}

/**
 * Build a SourceAcceptor that matches one specific captured window.
 * Use for per-call listeners where the legitimate sender is fixed at
 * request time (e.g. requestSnapshot captures the active iframe's
 * contentWindow before posting the snapshot request).
 */
export function acceptExpectedSource(
  expected: MessageEventSource | null,
): SourceAcceptor {
  return (source) => source !== null && source === expected
}

// ─── Main dispatcher hook ───────────────────────────────────────────────────

/**
 * Per-type handler. Bare function = "no origin check beyond the source
 * gate". Object form lets the consumer require an additional origin
 * match — needed for bridge-message types (papan:handshake, papan:run)
 * where the iframe source check is necessary but not sufficient:
 * sandboxed iframes can host attacker HTML whose origin differs from
 * the preview-origin URL we host. The origin check restricts to the
 * legitimate one.
 *
 * `requireOrigin` semantics:
 *   - `undefined` — no origin check
 *   - `null` — drop every message of this type (explicit "no valid
 *     origin configured", e.g. baseUrl not yet resolved; the handler
 *     should never run because we have nothing to compare against)
 *   - `string` — message origin must equal exactly
 */
export interface MessageHandlerConfig {
  handler: (e: MessageEvent) => void | Promise<void>
  requireOrigin?: string | null
}

export type MessageHandlerMap = Record<
  string,
  MessageHandlerConfig | ((e: MessageEvent) => void | Promise<void>)
>

/**
 * useGatedMessageDispatcher — registers a window.message listener that:
 *
 *   1. Drops messages without `e.data?.type`.
 *   2. Drops messages whose source isn't accepted by `acceptSource`.
 *   3. Looks up `e.data.type` in `handlers`; drops if no handler.
 *   4. For handlers configured with `requireOrigin`, drops if
 *      `e.origin` doesn't match.
 *   5. Calls the handler. Async handlers are awaited; errors are
 *      caught and logged so one malformed message can't break the
 *      listener for subsequent ones.
 *
 * `handlers` and `acceptSource` are read at dispatch time via refs, so
 * they can be inline objects/closures that change every render without
 * re-registering the underlying listener. The listener registers once
 * at mount and stays — there's no consumer-controllable deps array
 * because there's no state inside the listener body that would need
 * resetting on prop changes.
 */
export function useGatedMessageDispatcher(args: {
  acceptSource: SourceAcceptor
  handlers: MessageHandlerMap
  /** #1408 R6: an origin EVERY message must come from, on top of each type's own — read at
   *  dispatch time. A frame on a real origin (an app on its own preview origin) sets it; an
   *  opaque frame (origin 'null') leaves it undefined. */
  requireOrigin?: () => string | null | undefined
}): void {
  const handlersRef = useRef(args.handlers)
  handlersRef.current = args.handlers
  const acceptSourceRef = useRef(args.acceptSource)
  acceptSourceRef.current = args.acceptSource
  const requireOriginRef = useRef(args.requireOrigin)
  requireOriginRef.current = args.requireOrigin

  useEffect(() => {
    const listener = async (e: MessageEvent) => {
      if (!e.data?.type) return
      if (!acceptSourceRef.current(e.source)) return
      const frameOrigin = requireOriginRef.current?.()
      if (frameOrigin && e.origin !== frameOrigin) return

      const entry = handlersRef.current[e.data.type]
      if (!entry) return

      const handler = typeof entry === 'function' ? entry : entry.handler
      const requireOrigin = typeof entry === 'function' ? undefined : entry.requireOrigin
      // `requireOrigin === null` is the explicit "no valid origin
      // configured" signal — drop. `undefined` means caller didn't ask
      // for an origin check at all. A string is the normal compare.
      if (requireOrigin === null) return
      if (requireOrigin !== undefined && e.origin !== requireOrigin) return

      try {
        await handler(e)
      } catch (err) {
        // Swallow handler errors so one bad message doesn't break the
        // listener for subsequent ones. Surface in dev console.
        // eslint-disable-next-line no-console
        console.error('[iframe-message-gate] handler threw:', err)
      }
    }
    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [])
}

// ─── Per-call listener ──────────────────────────────────────────────────────

/**
 * addScopedMessageListener — install a temporary `window.message`
 * listener that only fires for messages from `expectedSource`. Returns
 * a cleanup function the caller invokes on completion or timeout.
 *
 * Use inside Promise constructors / interval callbacks where the
 * useGatedMessageDispatcher hook doesn't fit. Source-gates by exact
 * match against a captured window (typically the iframe.contentWindow
 * we sent the request to).
 */
export function addScopedMessageListener(
  expectedSource: MessageEventSource | null,
  handler: (e: MessageEvent) => void,
): () => void {
  function gated(e: MessageEvent) {
    // Reject null source regardless of `expectedSource` value — a null
    // expected window means we have nothing to compare against, so the
    // defensive default is to drop everything. Same shape as
    // `acceptExpectedSource`.
    if (!e.source || e.source !== expectedSource) return
    handler(e)
  }
  window.addEventListener('message', gated)
  return () => window.removeEventListener('message', gated)
}

// ─── Warn-once latch ────────────────────────────────────────────────────────

/**
 * useWarnOnce — returns a function that calls `console.warn` at most
 * once per component instance, per `key`. Used by handlers that
 * delegate to optional consumer callbacks (e.g. `onOpenFile`,
 * `onRequestOpenExternal`) — the latched warning tells a developer
 * that the message arrived but no callback was wired, so they can find
 * the missing prop instead of debugging a silent UX bug.
 *
 * The latch is per-instance: a remount resets it; the same instance
 * receiving N messages with the same key still warns only once.
 */
export function useWarnOnce(): (key: string, message: string) => void {
  const firedRef = useRef<Set<string>>(new Set())
  return (key, message) => {
    if (firedRef.current.has(key)) return
    firedRef.current.add(key)
    // eslint-disable-next-line no-console
    console.warn(message)
  }
}
